const SHANGHAI_OFFSET_MINUTES = 8 * 60;
export const DEFAULT_TIME_ZONE = 'Asia/Shanghai';

export function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

export function parseYmd(ymd: string): { year: number; month: number; day: number } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!match) {
    throw new Error(`Invalid date format: ${ymd}`);
  }
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
}

export function formatYmd(date: Date, timeZone = DEFAULT_TIME_ZONE): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function formatHm(date: Date, timeZone = DEFAULT_TIME_ZONE): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

export function ymdToUtcMidnight(ymd: string): number {
  const { year, month, day } = parseYmd(ymd);
  return Date.UTC(year, month - 1, day, 0, 0, 0, 0);
}

export function addDays(ymd: string, days: number): string {
  const ms = ymdToUtcMidnight(ymd) + days * 24 * 60 * 60 * 1000;
  return formatYmd(new Date(ms), 'UTC');
}

export function compareYmd(a: string, b: string): number {
  return ymdToUtcMidnight(a) - ymdToUtcMidnight(b);
}

export function differenceInDays(a: string, b: string): number {
  return Math.round((ymdToUtcMidnight(a) - ymdToUtcMidnight(b)) / (24 * 60 * 60 * 1000));
}

export function weekStartMonday(ymd: string): string {
  const { year, month, day } = parseYmd(ymd);
  const d = new Date(Date.UTC(year, month - 1, day));
  const dayOfWeek = (d.getUTCDay() + 6) % 7;
  return addDays(ymd, -dayOfWeek);
}

export function getWeekDates(weekStartYmd: string): string[] {
  return Array.from({ length: 7 }, (_, index) => addDays(weekStartYmd, index));
}

export function weekdayLabel(ymd: string): string {
  const date = new Date(ymdToUtcMidnight(ymd));
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'UTC',
    weekday: 'short',
  }).format(date);
}

export function formatDateLabel(ymd: string): string {
  const { year, month, day } = parseYmd(ymd);
  return `${year}年${pad2(month)}月${pad2(day)}日`;
}

export function isSameDay(a: string, b: string): boolean {
  return compareYmd(a, b) === 0;
}

export function toBeijingDateTime(ymd: string, time: string): Date {
  const { year, month, day } = parseYmd(ymd);
  const [hour, minute] = time.split(':').map((part) => Number(part));
  return new Date(Date.UTC(year, month - 1, day, hour, minute, 0, 0) - SHANGHAI_OFFSET_MINUTES * 60 * 1000);
}

export function toDateTimeLabel(startTime: string, endTime: string): string {
  return `${startTime}-${endTime}`;
}

export function todayYmd(timeZone = DEFAULT_TIME_ZONE): string {
  return formatYmd(new Date(), timeZone);
}

export function tomorrowYmd(timeZone = DEFAULT_TIME_ZONE): string {
  return addDays(todayYmd(timeZone), 1);
}

export function minutesOfDay(hm: string): number {
  const [hour, minute] = hm.split(':').map((part) => Number(part));
  return hour * 60 + minute;
}

export function formatRelativeWeek(weekStartYmd: string, baseYmd: string): string {
  const weeks = Math.floor(differenceInDays(weekStartYmd, baseYmd) / 7) + 1;
  return `第 ${weeks} 周`;
}
